import React, { useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { WhatsAppInbox } from '../WhatsAppInbox';

import type { ChatTarget } from '../WhatsAppWidget';

/**
 * /whatsapp - the DoubleTick-style inbox as a full page. The open chat lives
 * in the URL (`?c=<conversationId>` or `?lead=<leadId>`) so the widget bus,
 * notification taps and the bubble all land here by navigating. A lead's
 * name / destination (not in the URL) arrive via router state from the bus
 * or from the New-chat picker and are kept in local state.
 */
export const WhatsAppInboxPage = (): React.JSX.Element => {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const conversationId = params.get('c');
  const leadId = params.get('lead');
  const routerState = (location.state ?? null) as {
    name?: string | null;
    destination?: string | null;
  } | null;

  const [leadTarget, setLeadTarget] = useState<Extract<ChatTarget, { kind: 'lead' }> | null>(null);

  const target: ChatTarget | null = conversationId
    ? { kind: 'convo', id: conversationId }
    : leadId
      ? leadTarget?.id === leadId
        ? leadTarget
        : {
            kind: 'lead',
            id: leadId,
            name: routerState?.name ?? undefined,
            destination: routerState?.destination ?? undefined,
          }
      : null;

  const onTargetChange = (next: ChatTarget | null): void => {
    if (!next) {
      setLeadTarget(null);
      setParams({});
      return;
    }
    if (next.kind === 'convo') {
      setLeadTarget(null);
      setParams({ c: next.id });
      return;
    }
    setLeadTarget(next);
    setParams({ lead: next.id });
  };

  return (
    <div className="-mx-4 -my-6 h-[calc(100%+3rem)] lg:-mx-8 lg:-my-8 lg:h-[calc(100%+4rem)]">
      <WhatsAppInbox target={target} onTargetChange={onTargetChange} />
    </div>
  );
};
